import { EmailTemplateFormState } from '@/client/email-templates';
import { emailTemplateSchema } from '@/components/email-templates/forms/validations/email-template';
import { FormikInput, FormikSwitch } from '@/components/form';
import { FlexContainer } from '@/ui/styled-ui';
import { Field, Form, Formik, FormikValues, useFormikContext } from 'formik';
import { InputText } from 'primereact/inputtext';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type EmailTemplateInformationFormProps = {
  initialValues: EmailTemplateFormState;
  onSubmit: (data: FormikValues) => void;
  onInputChange: (values: EmailTemplateFormState) => void;
  onValidateForm: (isValid: boolean) => void;
  isSystem?: boolean;
};

type FormFieldsProps = Pick<
  EmailTemplateInformationFormProps,
  'onInputChange' | 'onValidateForm' | 'isSystem'
>;

const EmailFormFields: React.FC<FormFieldsProps> = ({
  onInputChange,
  onValidateForm,
  isSystem,
}) => {
  const { t } = useTranslation();
  const { values, isValid } = useFormikContext<EmailTemplateFormState>();

  useEffect(() => {
    onValidateForm(isValid);
  }, [isValid, onValidateForm]);

  useEffect(() => {
    onInputChange(values);
  }, [values, onInputChange]);

  return (
    <FlexContainer justify="flex-start" align="flex-start" direction="column">
      <Form className="w-full">
        <div className="field w-6 mb-4">
          <Field
            id="name"
            name="name"
            label={t('generic.name')}
            placeholder={t('templates.nameOf')}
            className="w-full"
            component={FormikInput}
            required
          />
        </div>
        <div className="field w-6  mb-4">
          <Field
            id="subject"
            name="subject"
            label={t('generic.subject')}
            placeholder={t('templates.subjectOf', { length: 190 })}
            className="w-full"
            component={FormikInput}
          />
        </div>
        <div className="field w-6 mb-4">
          <Field
            id="text"
            name="text"
            label={t('templates.altText')}
            placeholder={t('templates.textOf')}
            className="w-full"
            component={FormikInput}
          />
        </div>
        {isSystem && (
          <div className="field-checkbox">
            <Field
              inputId="active"
              name="active"
              label={t('templates.set.active')}
              component={FormikSwitch}
            />
          </div>
        )}

        {values.meta?.cloneOf && (
          <>
            <h3 className="mb-4">{t('generic.cloneOf')}</h3>

            <div className="field w-6">
              <label htmlFor="cloneOf">{t('templates.email')}</label>
              <InputText
                id="cloneOf"
                name="cloneOf"
                value={values.meta?.cloneOf}
                className="w-full"
                disabled
              />
            </div>

            <div className="field w-6">
              <label htmlFor="sourceCampaignId">{t('campaign')}</label>
              <InputText
                id="sourceCampaignId"
                name="sourceCampaignId"
                value={values.meta?.sourceCampaignId}
                className="w-full"
                disabled
              />
            </div>
          </>
        )}
      </Form>
    </FlexContainer>
  );
};

export const EmailTemplateInformationForm: React.FC<
  EmailTemplateInformationFormProps
> = ({ initialValues, onSubmit, onInputChange, onValidateForm, isSystem }) => {
  const { t } = useTranslation();

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={emailTemplateSchema(t)}
      onSubmit={onSubmit}
      validateOnMount
    >
      <EmailFormFields
        onInputChange={onInputChange}
        onValidateForm={onValidateForm}
        isSystem={isSystem}
      />
    </Formik>
  );
};
